<template>
  <div
    id="position"
    v-view-tracker="analyticsParams"
    class="position obs-el"
    data-attr="position"
  >
    Расположение
  </div>
  <div class="address">{{ sight.address }}</div>
  <div class="map-container">
    <NativeYandexMap
      v-if="sight.latitude && sight.longitude"
      :latitude="sight.latitude"
      :longitude="sight.longitude"
      :zoom="15"
    />
  </div>
  <div class="map-btn">
    <UiButton
      :variant="UiButtonVariant.SECONDARY"
      :color-mode="UiButtonColorMode.NEUTRAL"
      class="copy"
      text="Копировать"
      data-test="map-copy"
      is-full-width
      @clicked="copy"
    >
      <template #icon>
        <IconBase name="copy-20-icon-old" />
      </template>
    </UiButton>

    <div class="button-container">
      <UiButton
        ref="pathBtn"
        :variant="UiButtonVariant.SECONDARY"
        :color-mode="UiButtonColorMode.NEUTRAL"
        text="Маршрут"
        data-test="map-copy"
        is-full-width
        @clicked="changePopupStatus(true)"
      >
        <template #icon><IconBase name="route-20-icon" /></template>
      </UiButton>
      <div
        v-show="isOpenPopup"
        ref="popup"
        :key="tooltipPosition"
        :class="['path-content', tooltipPosition]"
      >
        <ul>
          <li
            v-for="popupEl in popupContent"
            :key="popupEl.icon"
            @click.stop="goTo(popupEl.url, popupEl.title)"
          >
            <IconBase :name="popupEl.icon" /> {{ popupEl.title }}
          </li>
        </ul>
      </div>
    </div>
  </div>

  <ui-toast
    :open="toastOpen"
    @close="toastClose"
  >
    <template #icon><IconBase name="copy-ok" /> </template>
    <template #content> {{ toastTitle }} </template>
  </ui-toast>
</template>

<script setup lang="ts">
  import { UiButton, UiButtonVariant, UiButtonColorMode } from '@experience/core-button-ui';
  import { UiToast } from '@experience/core-toast-ui';
  import { onClickOutside } from '@vueuse/core';
  import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';

  import { trackEvent, useViewTracker } from '@/services/analytics';
  import IconBase from '@/shared/icons/IconBase.vue';
  import { type City } from '@/shared/models/faq';
  import { type CityTagsAdvanced } from '@/shared/models/types';
  import NativeYandexMap from '@/shared/ui/NativeYandexMap.vue';
  import { useSightAnalyticsParams } from '@/shared/utils/analytics';

  const props = defineProps<{
    sight: CityTagsAdvanced;
    city: City;
  }>();

  const analyticsParams = useSightAnalyticsParams(() => props.sight);
  const vViewTracker = useViewTracker('attractions:sights:sight-popup:map.view');

  const onMapMessage = (event: MessageEvent) => {
    if (event.data?.source !== 'native-yandex-map') {
      return;
    }

    void trackEvent('attractions:sights:sight-popup:map.click', {
      ...analyticsParams.value,
      action_type: event.data.payload?.actionType ?? 'unknown',
    });
  };

  onMounted(() => {
    window.addEventListener('message', onMapMessage);
  });
  onBeforeUnmount(() => {
    window.removeEventListener('message', onMapMessage);
  });

  const popupContent = [
    {
      title: 'Яндекс Карты',
      url: `https://maps.yandex.ru/?text=${String(props.sight.latitude)},${String(props.sight.longitude)}&z=17`,
      icon: 'yandex-map-icon-24',
    },
    {
      title: '2ГИС',
      url: `https://2gis.ru/geo/${String(props.sight.longitude)}%2C${String(props.sight.latitude)}?m=${String(props.sight.longitude)}%2C${String(props.sight.latitude)}%2F17`,
      icon: '2gis-icon-24',
    },
    {
      title: 'Google maps',
      url: `https://www.google.com/maps/place/${String(props.sight.latitude)},${String(props.sight.longitude)}`,
      icon: 'google-map-icon-24',
    },
  ];

  const toastTitle = ref('');
  const toastOpen = ref(false);
  const isOpenPopup = ref(false);
  const popupRef = useTemplateRef('popup');
  const pathBtnRef = useTemplateRef('pathBtn');
  const tooltipPosition = ref('position-bottom');

  const toastClose = () => {
    toastOpen.value = false;
  };

  const changePopupStatus = (newStatus?: boolean, silent = false) => {
    if (!silent && !isOpenPopup.value) {
      void trackEvent('attractions:sights:sight-popup:route.click', analyticsParams.value);
    }

    if (typeof newStatus === 'boolean') {
      isOpenPopup.value = newStatus;
      const modalElement = document.querySelector<HTMLElement>('.ui-modal');

      if (!modalElement) {
        return;
      }

      const modalPosition: DOMRect = modalElement.getBoundingClientRect();
      tooltipPosition.value =
        modalPosition.height + modalPosition.top - (pathBtnRef.value?.$el.getBoundingClientRect().top ?? 0) > 235
          ? 'position-bottom'
          : 'position-top';

      return;
    }

    // setTimeout нужен. что бы клик по кнопке при открытой модалке закрывал ее, а не открывал заново из-за использования click outside
    setTimeout(() => {
      isOpenPopup.value = !isOpenPopup.value;
    }, 1);
  };

  const fullAddress = computed(() => {
    const city = props.city.name_ru ? `${props.city.name_ru},` : '';
    const address = props.sight.address;

    if (address) {
      return `${props.city.name_ru}, ${address}`;
    }

    return address ? `${city} ${address}` : '';
  });

  onClickOutside(popupRef, () => {
    if (isOpenPopup.value) {
      changePopupStatus();
    }
  });

  const copy = () => {
    void trackEvent('attractions:sights:sight-popup:copy-location.click', analyticsParams.value);
    void navigator.clipboard
      .writeText(fullAddress.value)
      .then(() => {
        toastTitle.value = 'Адрес скопирован';
        toastOpen.value = true;
      })
      .catch(() => {
        toastTitle.value = 'Ошибка при копировании';
        toastOpen.value = true;
      });
  };

  const goTo = (link: string, title?: string) => {
    void trackEvent('attractions:sights:sight-popup:route-service.select', {
      route_service: title,
      ...analyticsParams.value,
    });
    window.open(link, '_blank');
    changePopupStatus(undefined, true);
  };
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as legacy-typography;

  .position {
    margin-top: var(--alias-space-48);

    @include legacy-typography.base-600-500-extra;
  }

  .address {
    @include legacy-typography.base-400-400-regular;
  }

  .map-container {
    width: 100%;
    height: 343px;
    margin-top: var(--alias-space-16);
    overflow: hidden;
    border-radius: var(--alias-space-8);
  }

  .map-btn {
    display: flex;
    gap: var(--alias-space-8);
    margin-top: var(--alias-space-8);

    @media screen and (width <= 360px) {
      flex-direction: column;
    }
  }

  :deep(.inline-block) {
    width: 100% !important;
    margin: 0 !important;
    border: 0 !important;
  }

  .button-container {
    position: relative;
  }

  .copy,
  .button-container {
    flex-basis: 50%;
  }

  .path-content {
    position: absolute;
    right: 0;
    z-index: 4;
    width: 100%;
    min-width: 272px;
    padding: var(--alias-space-16) var(--alias-space-24);
    background: var(--alias-color-background-default);
    border-radius: var(--alias-radius-8);
    box-shadow: 0 0 18px 0 var(--global-color-transparent-black-a500);

    ul {
      li {
        display: flex;
        gap: var(--alias-space-8);
        padding: var(--alias-space-12) 0;
        cursor: pointer;

        @include legacy-typography.base-400-400-regular;
      }
    }
  }

  .position-top {
    bottom: calc(100% + 8px);
  }

  .position-bottom {
    top: calc(100% + 8px);
  }

  :deep(.toast-inner) {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.icon) {
    display: flex;
    align-items: center;
    height: 24px;
  }
</style>
